import { useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { useSeo } from '../hooks/useSeo'
import { SITE } from '../data/site'

export function AboutPage() {
  useSeo({
    title: 'எங்களைப் பற்றி',
    description: 'தெய்வீக ஓசை YouTube சேனல் மற்றும் இணையதளம் பற்றிய அறிமுகம்.',
    path: '/about',
  })

  return (
    <div className="container section prose">
      <Breadcrumbs items={[{ label: 'முகப்பு', to: '/' }, { label: 'எங்களைப் பற்றி' }]} />
      <h1>தெய்வீக ஓசை பற்றி</h1>
      <p>
        <strong>{SITE.nameTa}</strong> ({SITE.nameEn}) என்பது தமிழ் பக்திப் பாடல்கள், ஸ்துதிகள் மற்றும் ஆன்மீக
        அறிவைப் பகிரும் YouTube சேனலின் அதிகாரப்பூர்வ இணையதளம்.
      </p>
      <p>எங்கள் நோக்கம்:</p>
      <ul>
        <li>அதிக பக்தர்களை YouTube சேனலுக்கு அழைப்பது</li>
        <li>நிலையான தமிழ் பக்தி உள்ளடக்க நூலகத்தை உருவாக்குவது</li>
        <li>எதிர்கால வளர்ச்சிக்கான எளிய, வேகமான தளத்தை அமைப்பது</li>
      </ul>
      <p>
        <a href={SITE.youtubeUrl} target="_blank" rel="noopener noreferrer">
          YouTube சேனலைப் பார்வையிடுங்கள்
        </a>
      </p>
    </div>
  )
}

export function ContactPage() {
  useSeo({ title: 'தொடர்பு', description: 'தெய்வீக ஓசையைத் தொடர்பு கொள்ளுங்கள்.', path: '/contact' })
  const [sent, setSent] = useState(false)

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)
    const subject = encodeURIComponent(`[${data.get('topic')}] ${data.get('name')}`)
    const body = encodeURIComponent(
      `பெயர்: ${data.get('name')}\nமின்னஞ்சல்: ${data.get('email')}\nதலைப்பு: ${data.get('topic')}\n\n${data.get('message')}`,
    )
    window.location.href = `mailto:${SITE.email}?subject=${subject}&body=${body}`
    setSent(true)
  }

  return (
    <div className="container section">
      <Breadcrumbs items={[{ label: 'முகப்பு', to: '/' }, { label: 'தொடர்பு' }]} />
      <div className="section-header">
        <h1>தொடர்பு</h1>
        <p>
          மின்னஞ்சல்: <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
        </p>
      </div>
      <form className="contact-form" onSubmit={onSubmit}>
        <label>
          பெயர்
          <input name="name" required autoComplete="name" />
        </label>
        <label>
          மின்னஞ்சல்
          <input name="email" type="email" required autoComplete="email" />
        </label>
        <label>
          தலைப்பு
          <select name="topic" required defaultValue="general">
            <option value="general">பொது வினவல்</option>
            <option value="lyrics">வரி திருத்தம்</option>
            <option value="copyright">பதிப்புரிமை</option>
            <option value="artwork">படம் / வடிவமைப்பு</option>
            <option value="content">உள்ளடக்கத் திருத்தம்</option>
          </select>
        </label>
        <label>
          செய்தி
          <textarea name="message" rows={5} required />
        </label>
        <button type="submit" className="btn btn--primary">
          அனுப்பு
        </button>
        {sent && <p role="status">உங்கள் மின்னஞ்சல் செயலியைத் திறக்கிறோம்…</p>}
      </form>
    </div>
  )
}

function LegalShell({
  title,
  path,
  children,
}: {
  title: string
  path: string
  children: ReactNode
}) {
  useSeo({ title, path })
  return (
    <div className="container section prose">
      <Breadcrumbs items={[{ label: 'முகப்பு', to: '/' }, { label: title }]} />
      <h1>{title}</h1>
      {children}
      <p>
        <Link to="/contact">தொடர்பு பக்கம்</Link>
      </p>
    </div>
  )
}

export function PrivacyPage() {
  return (
    <LegalShell title="தனியுரிமைக் கொள்கை" path="/privacy">
      <p>
        இந்த இணையதளம் பக்கப் பார்வைகள் மற்றும் பதிவிறக்கங்களைப் புரிந்துகொள்ள பகுப்பாய்வு கருவிகளைப்
        பயன்படுத்தலாம். தனிப்பட்ட தரவு விற்பனை செய்யப்படுவதில்லை.
      </p>
      <p>தொடர்புப் படிவம் மூலம் நீங்கள் அனுப்பும் விவரங்கள் வினவலுக்குப் பதிலளிக்க மட்டுமே பயன்படுத்தப்படும்.</p>
    </LegalShell>
  )
}

export function DisclaimerPage() {
  return (
    <LegalShell title="பொறுப்புத்துறப்பு" path="/disclaimer">
      <p>
        இத்தளத்தில் உள்ள ஆன்மீக தகவல்கள் பொது அறிமுகம் மட்டுமே. பழக்க வழக்கங்கள் பிராந்தியம் / குடும்பத்திற்கு ஏற்ப
        மாறலாம்.
      </p>
      <p>ஆரோக்கியம், நிதி அல்லது வாழ்க்கை முடிவுகள் உத்தரவாதம் அளிக்கப்படுவதில்லை.</p>
    </LegalShell>
  )
}

export function TermsPage() {
  return (
    <LegalShell title="பயன்பாட்டு விதிகள்" path="/terms">
      <p>இத்தள உள்ளடக்கத்தை தனிப்பட்ட பக்திப் பயன்பாட்டிற்கும் வணிகமற்ற பகிர்விற்கும் பயன்படுத்தலாம்.</p>
      <p>வணிக மறுவிற்பனை, மாற்றம் அல்லது அனுமதியின்றி மறுபதிப்பு அனுமதிக்கப்படாது.</p>
    </LegalShell>
  )
}

export function CopyrightPage() {
  return (
    <LegalShell title="பதிப்புரிமைக் கொள்கை" path="/copyright">
      <p>
        தெய்வீக ஓசை அசல் பாடல்கள், வடிவமைப்பு மற்றும் தள உள்ளடக்கத்திற்குப் பதிப்புரிமை {SITE.nameEn}-க்கு உரியது
        (பொருந்தும் வகையில்).
      </p>
      <p>பாரம்பரிய ஸ்துதிகள் பொதுவான பாரம்பரியப் பொருள்களாக இருக்கலாம்; உரைத் தொகுப்பு மற்றும் பொருள் விளக்கம் தளத்திற்கு உரியவை.</p>
      <p>பதிப்புரிமைக் கவலைகள்: {SITE.email}</p>
    </LegalShell>
  )
}
